import {
  FC, PropsWithChildren
  , useLayoutEffect
  , useRef
} from 'react'
import ReactDataSheet from 'react-datasheet'
import { Button } from '@progress/kendo-react-buttons'
import { Checkbox } from '@progress/kendo-react-inputs'
import { DropDownList } from '@progress/kendo-react-dropdowns'

import 'react-datasheet/lib/react-datasheet.css'

import hearingMessage from '../../../data/hearing-message.json'
import { CodeName } from '../../../services/initializing/CodeName'
import { ConfirmReplyActions, useAppDispatch, useAppSelector } from '../../../stores'
import { WorkQuantity } from '@gateway.Supplier'
import { Tooltip } from '@progress/kendo-react-tooltip'

export type CellValue = number | null

export interface CellData extends ReactDataSheet.Cell<CellData, number> {
  value: number | null
  readOnly: boolean
}

export interface GridElement
  extends ReactDataSheet.Cell<GridElement, CellValue> {
  value: CellValue
}
export interface GridDataSate {
  grid: GridElement[][]
  workQuantityList: WorkQuantity[]
  workQuantityListLastMonth: WorkQuantity[]
  displayModes: boolean[]
}

/** メインのグリッド設定 */
const SheetRenderer: FC<PropsWithChildren<{
  className: string
  simulationYm: string
  years: number
  months: number[]
  displayStartYear: string
}>
> = ({ className, simulationYm, children, years, months, displayStartYear }) => {
  const ref = useRef<HTMLDivElement>(null)
  const location = useAppSelector((state) => state.confirmreply.location.supplierDecisionBuildingLocation)
  const locationTop = useAppSelector((state) => state.confirmreply.locationTop.supplierDecisionBuildingLocation)
  const dispatch = useAppDispatch()

  const createYearColumns = (startYear: string) => {
    const firstYear = Number(startYear)
    const emptyArray = Array.from({ length: years })
    return emptyArray.map<number>((_value, index) => {
      return firstYear + index
    })
  }

  useLayoutEffect(() => {
    ref.current?.scrollTo({ left: location, top: locationTop })
  }, [location, locationTop])

  return (
    <div
      ref={ref}
      className="scrollable-container"
      style={{ maxHeight: '62vh' }}
      onScroll={(event) => {
        dispatch(ConfirmReplyActions.setSupplierDecisionBuildingLocation(Number(ref.current?.scrollLeft)))
        dispatch(ConfirmReplyActions.setSupplierDecisionBuildingLocationTop(Number(ref.current?.scrollTop)))
      }}
    >
      <table
        className={className}
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr>
            <th rowSpan={2} style={{ position: 'sticky', left: 0 }}>
              <div style={{ width: 30 }}>No</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 33 }}>
              <div style={{ width: 40 }}>非表示</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 76 }}>
              <div style={{ width: 40 }}>対象外</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 119 }}>
              <div style={{ width: 60 }}>詳細</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 182 }}>
              <div style={{ width: 85 }}>複製</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 270 }}>
              <div style={{ width: 60 }}>管轄支店</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 333 }}>
              <div style={{ width: 270 }}>工事名称</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 606 }}>
              <div style={{ width: 85 }}>施工部位</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 694 }}>
              <div style={{ width: 70 }}>工程状況</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 767 }}>
              <div style={{ width: 122 }}>遅延程度</div>
            </th>
            <th rowSpan={2} style={{ position: 'sticky', left: 892 }}>
              <div style={{ width: 80 }}>直手/応援</div>
            </th>
            {createYearColumns(displayStartYear).map((year) => (
              <th colSpan={12} key={year}>
                {year}年度
              </th>
            ))}
          </tr>
          <tr>
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
            {months.map((m) => (
              <th key={m} className="calendar-month">
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

/** 左ヘッダの設定 */
const RowRenderer: FC<PropsWithChildren<{
  expenseCd: string
  displaySelected: boolean
  display: string
  rowNo: number
  rowLength: number
  workQuantity: WorkQuantity
  nonCtrlFlg: boolean
  completedIndexList: number[]
  onBuildingInfoBtnClick: (workQuantity: WorkQuantity) => void
  onDecidedDisplayCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onDecidedApplicabCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onConstPartDropDownChange: (val: string, rowNo: number) => void
  onPrssStsDropDownChange: (val: string, rowNo: number) => void
  onDelayLevelCdDropDownChange: (val: string, rowNo: number) => void
  onWorkerKbnDropDownChange: (val: string, rowNo: number) => void
  onDuplicationBtnClick: (workQuantity: WorkQuantity, rowNo: number) => void
  onUndoDuplicationBtnClick: (workQuantity: WorkQuantity, rowNo: number) => void
}>> = ({
  expenseCd,
  displaySelected,
  display,
  rowNo,
  rowLength,
  workQuantity,
  nonCtrlFlg,
  completedIndexList,
  children,
  onBuildingInfoBtnClick,
  onDecidedDisplayCheckboxChange,
  onDecidedApplicabCheckboxChange,
  onConstPartDropDownChange,
  onPrssStsDropDownChange,
  onDelayLevelCdDropDownChange,
  onWorkerKbnDropDownChange,
  onDuplicationBtnClick,
  onUndoDuplicationBtnClick
}) => {
  const isLastRow = (rowNo === rowLength - 1 || rowNo === rowLength - 2 || rowNo === rowLength - 3)
  const userProp = useAppSelector((state) => state.common.userProp)

  // ログイン情報．権限区分＝"5"(業者)の場合、M1非表示を非表示します。
  const authorityDivisionHidden = userProp?.authority !== 5

  // M1明細の各行に対し、State．新規追加フラグ=1(新規)の場合は赤字で表示する
  const isRed = workQuantity !== undefined ? workQuantity.newAddFlg === 1 : false

  const initialData = useAppSelector((x) => x.common.initialData)
  const branchesData = initialData.codeNames('SI01')
  const constructionPart = [
    {
      CODE_KBN: 'SE02',
      CODE: '',
      NAME: '',
      ABBREVIATION: '',
      DISPLAY_ORDER: 0,
      INVALID_FLG: 0
    },
    ...initialData.codeNames('SE02')
  ]
  const processSts = [
    {
      CODE_KBN: 'KO05',
      CODE: '',
      NAME: '',
      ABBREVIATION: '',
      DISPLAY_ORDER: 0,
      INVALID_FLG: 0
    },
    ...initialData.codeNames('KO05')
  ]
  const delayLevel = [
    {
      CODE_KBN: 'TI01',
      CODE: '',
      NAME: '',
      ABBREVIATION: '',
      DISPLAY_ORDER: 0,
      INVALID_FLG: 0
    },
    ...initialData.codeNames('TI01')
  ]
  const workerKbn = initialData.codeNames('JI01').filter(x => x.NUM1 === 1) // 表示フラグがONのデータのみを表示する

  // コード名称リストから名称を取得する
  const createValue = (codeNames: any[], codeValue?: string | null) => {
    if (codeValue === undefined) {
      return null
    }
    const _value = codeNames.find((codeName) => codeName.CODE === codeValue)

    return _value === undefined ? null : _value
  }

  if (isLastRow) {
    const name = rowNo === rowLength - 3
      ? '直手小計'
      : rowNo === rowLength - 2
        ? '応援小計'
        : rowNo === rowLength - 1
          ? '総合計'
          : ''
    return (
      <tr style={{ height: 33 }}>
        <th
          style={{ position: 'sticky', left: 0, textAlign: 'left' }}
          colSpan={11}
        >
          <span>{name}</span>
        </th>
        {children}
      </tr>
    )
  }

  // 竣工済物件の分を詰めた行番号の算出
  const no = (rowNo + 1) - completedIndexList.filter(i => i < rowNo).length

  if (workQuantity.completedFlg === 1) {
    return (<></>)
  }

  return (
    <tr style={{ height: 33, display: `${display}` }}>
      <th style={{ position: 'sticky', left: 0, color: isRed ? '#F00' : '' }}>
        {no}
      </th>
      <th style={{ position: 'sticky', left: 33 }}>
        {authorityDivisionHidden && (
       <Checkbox
       checked={displaySelected}
       disabled={workQuantity.lostOrderFlg === 1 || nonCtrlFlg}
       onChange={(e) => onDecidedDisplayCheckboxChange(rowNo, e.value)}
     />
        )}
      </th>
      <th style={{ position: 'sticky', left: 76 }}>
        {authorityDivisionHidden && (
          <Checkbox
            checked={workQuantity.notApplicableFlg === 1}
            disabled={workQuantity.lostOrderFlg === 1 || nonCtrlFlg}
            onChange={(e) => onDecidedApplicabCheckboxChange(rowNo, e.value)}
          />
        )}
      </th>
      <th style={{ position: 'sticky', left: 119 }}>
        <Button
          style={{ color: isRed ? '#F00' : '' }}
          onClick={() => onBuildingInfoBtnClick(workQuantity)}
        >
          詳細
        </Button>
      </th>
      {(workQuantity.isCopied === 1) // 複製による新規行の場合
        ? (
        <th style={{ position: 'sticky', left: 182 }}>
          <Button
            style={{ color: isRed ? '#F00' : '' }}
            onClick={() => onUndoDuplicationBtnClick(workQuantity, rowNo)}
          >
            複製取消
          </Button>
        </th>
          )
        : (
        <th style={{ position: 'sticky', left: 182 }}>
          <Button
            style={{ color: isRed ? '#F00' : '' }}
            onClick={() => onDuplicationBtnClick(workQuantity, rowNo)}
            disabled={workQuantity.lostOrderFlg === 1 || nonCtrlFlg || workQuantity.count === 2}
          >
            複製
          </Button>
        </th>
          )}

      <th style={{ position: 'sticky', left: 270, color: isRed ? '#F00' : '' }}>
        {((workQuantity.count === 2 && workQuantity.workerKbn === '2' && workQuantity.usedAsCopySource === 0) || // 一度保存したのちの「応援」行を空にする。SELECTの際のorderbyにより必ず「応援」が2行目になることを利用する
        (workQuantity.isCopied === 1)) // 複製による新規行を空にする
          ? (
          <span></span>
            )
          : (
        <span>
        {
          branchesData.find((item: CodeName) => {
            return item.CODE === workQuantity.branchCd
          })?.NAME
        }
        </span>
            )}
      </th>
      <th style={{ position: 'sticky', left: 333, color: isRed ? '#F00' : '' }}>
        {((workQuantity.count === 2 && workQuantity.workerKbn === '2' && workQuantity.usedAsCopySource === 0) || // 一度保存したのちの「応援」行を空にする。SELECTの際のorderbyにより必ず「応援」が2行目になることを利用する
        (workQuantity.isCopied === 1)) // 複製による新規行を空にする
          ? (
          <span></span>
            )
          : (
          <Tooltip position='right'>
            <span title={workQuantity.constructionNm ?? ''} style={{ color: isRed ? '#F00' : '', width: 270, display: 'block', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>{workQuantity.constructionNm}</span>
          </Tooltip>
            )}
      </th>
      <th style={{ position: 'sticky', left: 606 }}>
        <DropDownList
          data={constructionPart}
          dataItemKey="CODE"
          textField="NAME"
          value={createValue(constructionPart, workQuantity.constructionPartCd)}
          disabled={workQuantity.lostOrderFlg === 1 ||
            nonCtrlFlg ||
            (workQuantity.count === 2 && workQuantity.workerKbn === '2' && workQuantity.usedAsCopySource === 0) || // 一度保存したのちの「応援」行を空にする。SELECTの際のorderbyにより必ず「応援」が2行目になることを利用する
            (workQuantity.isCopied === 1)} // 複製による新規行のみが対象
          onChange={(e) => {
            onConstPartDropDownChange(e.value.CODE, rowNo)
          }}
          style={{ fontSize: 12, color: isRed ? '#F00' : '' }}
        />
      </th>
      <th style={{ position: 'sticky', left: 694 }}>
        <DropDownList
          data={processSts}
          dataItemKey="CODE"
          textField="NAME"
          value={createValue(processSts, workQuantity.processStsCd)}
          disabled={workQuantity.lostOrderFlg === 1 ||
            nonCtrlFlg ||
            (workQuantity.count === 2 && workQuantity.workerKbn === '2' && workQuantity.usedAsCopySource === 0) || // 一度保存したのちの「応援」行を空にする。SELECTの際のorderbyにより必ず「応援」が2行目になることを利用する
            (workQuantity.isCopied === 1)} // 複製による新規行のみが対象
          onChange={(e) => {
            onPrssStsDropDownChange(e.value.CODE, rowNo)
          }}
          style={{ fontSize: 12, color: isRed ? '#F00' : '' }}
        />
      </th>
      <th style={{ position: 'sticky', left: 767 }}>
        <DropDownList
          data={delayLevel}
          dataItemKey="CODE"
          textField="NAME"
          value={createValue(delayLevel, workQuantity.delayLevelCd)}
          disabled={workQuantity.lostOrderFlg === 1 ||
            nonCtrlFlg ||
            (workQuantity.count === 2 && workQuantity.workerKbn === '2' && workQuantity.usedAsCopySource === 0) || // 一度保存したのちの「応援」行を空にする。SELECTの際のorderbyにより必ず「応援」が2行目になることを利用する
            (workQuantity.isCopied === 1)} // 複製による新規行のみが対象
          onChange={(e) => {
            onDelayLevelCdDropDownChange(e.value.CODE, rowNo)
          }}
          style={{ fontSize: 12, color: isRed ? '#F00' : '' }}
        />
      </th>
      <th style={{ position: 'sticky', left: 892 }}>
        <DropDownList
          data={workerKbn}
          dataItemKey="CODE"
          textField="NAME"
          value={createValue(workerKbn, workQuantity.workerKbn)}
          disabled={workQuantity.lostOrderFlg === 1 || nonCtrlFlg || workQuantity.count === 2 || workQuantity.updDtm !== null}
          onChange={(e) => {
            onWorkerKbnDropDownChange(e.value.CODE, rowNo)
          }}
          style={{ fontSize: 12, color: isRed ? '#F00' : '' }}
        />
      </th>
      {children}
    </tr>
  )
}

/** セルの設定 */
const ValueViewer: FC<{
  valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  rowLength: number
  isRed: boolean
  isBackground: boolean
  isNonCtrlFlg: boolean
}> = ({ valueViewerProps, rowLength, isRed, isBackground, isNonCtrlFlg }) => {
  const isLastRow = valueViewerProps.row === rowLength - 1
  const isOuenTotalRow = valueViewerProps.row === rowLength - 2
  return (
    <div
      style={{
        backgroundColor: isLastRow ? '#99FF99' : isOuenTotalRow ? '#E6FFE6' : isNonCtrlFlg ? '' : valueViewerProps.cell.readOnly === true ? '' : isBackground ? '#E6FFE6' : '',
        height: '100%',
        color: isRed ? '#F00' : ''
      }}
    >
      {valueViewerProps.value}
    </div>
  )
}

interface BuildingDecidedBySupplierGridProps {
  gridDataState: GridDataSate
  years: number
  months: number[]
  simulationYm: string
  displayStartYear: string
  nonCtrlFlg: boolean
  expenseCd: string
  onCellsChanged: (
    arrayOfChanges: ReactDataSheet.CellsChangedArgs<GridElement, CellValue>,
    arrayOfAdditions?: ReactDataSheet.CellAdditionsArgs<CellValue>
  ) => void
  onBuildingInfoBtnClick: (workQuantity: WorkQuantity) => void
  onDecidedDisplayCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onDecidedApplicabCheckboxChange: (selectedRowIndex: number, selected: boolean) => void
  onConstPartDropDownChange: (val: string, rowNo: number) => void
  onPrssStsDropDownChange: (val: string, rowNo: number) => void
  onDelayLevelCdDropDownChange: (val: string, rowNo: number) => void
  onWorkerKbnDropDownChange: (val: string, rowNo: number) => void
  onDuplicationBtnClick: (workQuantity: WorkQuantity, rowNo: number) => void
  onUndoDuplicationBtnClick: (workQuantity: WorkQuantity, rowNo: number) => void
}

export const BuildingDecidedBySupplierGrid: FC<BuildingDecidedBySupplierGridProps> = (props) => {
  const initialData = useAppSelector((x) => x.common.initialData)
  const expenses = initialData.codeNames('HI01')
  const unit = expenses.find((item: CodeName) => {
    return item.CODE === props.expenseCd
  })?.TXT8
  const sheetRenderer = (
    sheetRendererProps: ReactDataSheet.SheetRendererProps<
    GridElement,
    CellValue
    >
  ) => {
    return (
      <SheetRenderer
        className={sheetRendererProps.className}
        simulationYm={props.simulationYm}
        years={props.years}
        months={props.months}
        displayStartYear={props.displayStartYear}
      >
        {sheetRendererProps.children}
      </SheetRenderer>
    )
  }

  const rowRenderer = (
    rowRendererProps: ReactDataSheet.RowRendererProps<
    GridElement,
    CellValue
    > & { selected: boolean }
  ) => {
    const workQuantity: WorkQuantity =
      props.gridDataState.workQuantityList[rowRendererProps.row]
    const currentRowDisplaySelection =
      workQuantity !== undefined ? workQuantity.hiddenFlg !== 0 : false
    const display = props.gridDataState.displayModes[rowRendererProps.row]
      ? ''
      : 'none'
    // 竣工済物件のインデックス番号
    const completedIndexList = props.gridDataState.workQuantityList.flatMap((w, i) => (w.completedFlg === 1 ? i : []))

    return (
      <RowRenderer
        expenseCd={props.expenseCd}
        rowNo={rowRendererProps.row}
        rowLength={props.gridDataState.grid.length}
        workQuantity={workQuantity}
        displaySelected={currentRowDisplaySelection}
        display={display}
        nonCtrlFlg={props.nonCtrlFlg}
        completedIndexList={completedIndexList}
        onBuildingInfoBtnClick={props.onBuildingInfoBtnClick}
        onDecidedDisplayCheckboxChange={props.onDecidedDisplayCheckboxChange}
        onDecidedApplicabCheckboxChange={props.onDecidedApplicabCheckboxChange}
        onConstPartDropDownChange={props.onConstPartDropDownChange}
        onPrssStsDropDownChange={props.onPrssStsDropDownChange}
        onDelayLevelCdDropDownChange={props.onDelayLevelCdDropDownChange}
        onWorkerKbnDropDownChange={props.onWorkerKbnDropDownChange}
        onDuplicationBtnClick={props.onDuplicationBtnClick}
        onUndoDuplicationBtnClick={props.onUndoDuplicationBtnClick}
      >
        {rowRendererProps.children}
      </RowRenderer>
    )
  }

  const valueViewer = (
    valueViewerProps: ReactDataSheet.ValueViewerProps<GridElement, number>
  ) => {
    const workQuantity: WorkQuantity = props.gridDataState.workQuantityList[valueViewerProps.row]
    let isRed = false
    let isBackground = false

    if (workQuantity !== undefined) {
      if (workQuantity.workerKbn === '2') {
        isBackground = true
      }
      if (workQuantity.newAddFlg === 1) {
        // M1明細の各行に対し、State．新規追加フラグ=1(新規)の場合は赤字で表示する
        isRed = true
      } else {
        const colNo = valueViewerProps.col
        const workQuantityListLastMonth = props.gridDataState.workQuantityListLastMonth
        const lastMonth = workQuantityListLastMonth.find((obj) =>
          obj.procurementNo === workQuantity.procurementNo && obj.subNo === workQuantity.subNo && obj.expenseSeq === workQuantity.expenseSeq && obj.workerKbn === workQuantity.workerKbn)

        // ※対象の支店コードに一致する変数．前月度労務能力．業者決定物件．月別労務数が取得出来る場合
        if (workQuantity !== undefined && lastMonth !== undefined) {
          const simulatedMonth = Number(props.simulationYm.substring(4, props.simulationYm.length))

          // ※データストア．業者ヒアリング遷移パラメータ．パラメータ．シミュレーション年月度が4月度の場合、
          // 変数．前月度労務能力．業者決定物件．月別労務数のIndexを+12する
          if (simulatedMonth === 4) {
            if (colNo + 12 <= workQuantity.workQuantityByMonth.length - 1) {
              isRed = workQuantity.workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo + 12]
            } else {
              isRed = true
            }
          } else {
            isRed = workQuantity.workQuantityByMonth[colNo] !== lastMonth?.workQuantityByMonth[colNo]
          }
        }
      }
    }

    return (
      <ValueViewer
        valueViewerProps={valueViewerProps}
        rowLength={props.gridDataState.grid.length}
        isRed={isRed}
        isBackground={isBackground}
        isNonCtrlFlg={props.nonCtrlFlg}
      />
    )
  }

  return (
    <div style={{ position: 'sticky', zIndex: 3 }}>
      <h3 style={{ marginBottom: 0 }}>
        大成決定物件（単位：{unit}）
        <span
            style={{ marginLeft: '16px', color: '#d51923', fontWeight: '700', fontSize: '16px' }}
          >
          {hearingMessage.buildingDecidedBySupplier.buildingDecidedBySupplierAnnotationInfo}
        </span>
      </h3>
      <ReactDataSheet<GridElement, CellValue>
        data={props.gridDataState.grid}
        sheetRenderer={sheetRenderer}
        rowRenderer={rowRenderer}
        valueRenderer={(cell) => cell.value}
        valueViewer={valueViewer}
        onCellsChanged={props.onCellsChanged}
      />
    </div>
  )
}
